import { FlexTableExtension } from "../flex-table";
import { cloneCell, computeMaxWidth, offset } from '../util'

import throttle from 'lodash/throttle'

const sixtyFpsInMs = 1000 / 60

/** @typedef {import("../flex-table").FlexTableContext} FlexTableContext */

export class FixedHeaderExtension extends FlexTableExtension {
    constructor() {
        super()
        this.isHeaderAffixed = false
    }

    /**
     * Attach the fixed header extension to the context.
     * @param {FlexTableContext} flexTableContext The flex table context object
     */
    attach(flexTableContext) {
        if (this.isAttached) return
        super.attach(flexTableContext)
        this.flexTableContext = flexTableContext

        this.scrollingHeaderTable = this.createScrollingHeaderTable()
        this.scrollingHeaderTable.classList.add("flex-scrolling-header-table")
        this.scrollingHeaderTable.tHead.style.background = "white" // TODO: Extract style to directive level.

        flexTableContext.verticalContainer.appendChild(this.scrollingHeaderTable)

        // Expose the table on the context.
        flexTableContext.scrollingHeaderTable = this.scrollingHeaderTable

        this.scrollContainerScroll = throttle(() => {
            if (this.shouldAffixHeader()) {
                if (!this.isHeaderAffixed) {
                    this.affixHeader()
                }
            } else if (this.isHeaderAffixed) {
                this.detachHeader()
            }

            // Synchronize the scroll position of the fixed header and scrolling container.
            this.scrollingHeaderTable.scrollLeft = this.horizontalContainer.scrollLeft
        }, sixtyFpsInMs)

        this.horizontalContainerScroll = throttle(() => {
            // Synchronize the scroll position of the fixed header and scrolling container.
            this.scrollingHeaderTable.scrollLeft = this.horizontalContainer.scrollLeft
        }, sixtyFpsInMs)

        // Check the table dimensions against their older values 60 times a second.
        // Track both scrollWidth (content width) AND the container's outer width, so a
        // viewport/browser resize that reflows columns without changing scrollWidth still
        // triggers a re-sync of the cloned header.
        // There may be some other more performant way using mutation observers.
        let originalSize = flexTableContext.verticalContainer.scrollWidth
        let originalWidth = flexTableContext.verticalContainer.offsetWidth
        this.keepDimensionsSynced = window.setInterval(() => {
            const newSize = flexTableContext.verticalContainer.scrollWidth
            const newWidth = flexTableContext.verticalContainer.offsetWidth

            if (originalSize !== newSize || originalWidth !== newWidth) {
                originalSize = newSize
                originalWidth = newWidth
                this.resync()
            }
        }, sixtyFpsInMs)

        // Re-measure and reposition the cloned header whenever the viewport/browser is resized,
        // so its width, left offset, and per-column widths track the reflowed body table.
        this.handleResize = throttle(() => this.resync(), sixtyFpsInMs)
        window.addEventListener("resize", this.handleResize)

        // Listen in the CAPTURE phase at the document level so scrolls inside ANY inner
        // overflow container (not just the window) trigger affix re-evaluation. Scroll
        // events don't bubble, but they do fire during the capture phase.
        document.addEventListener("scroll", this.scrollContainerScroll, true)
        flexTableContext.horizontalContainer.addEventListener("scroll", this.horizontalContainerScroll)

        // Fire initial events
        this.scrollContainerScroll()
        this.horizontalContainerScroll()
    }

    /**
     * Detach the fixed header extension from the context.
     * @param {FlexTableContext} flexTableContext The flex table context object
     */
    detach(flexTableContext) {
        if (!this.isAttached) return

        this.detachHeader();
        document.removeEventListener("scroll", this.scrollContainerScroll, true)
        flexTableContext.horizontalContainer.removeEventListener("scroll", this.horizontalContainerScroll)
        window.clearInterval(this.keepDimensionsSynced)
        window.removeEventListener("resize", this.handleResize)
        this.scrollingHeaderTable.remove()
        flexTableContext.scrollingHeaderTable = null
        super.detach()
    }

    /**
     * Rebuild the cloned header (re-measuring per-column widths against the possibly
     * reflowed body table) and reposition it with fresh geometry. Safe to call on
     * resize / content-size changes; no-ops if the extension is detached.
     */
    resync() {
        if (!this.isAttached || !this.scrollingHeaderTable) return

        // Drop the affixed positioning before re-measuring so getComputedStyle reads the
        // natural body-cell widths rather than the fixed-overlay's stale ones.
        if (this.isHeaderAffixed) this.detachHeader()

        // Replace the cloned header table with a freshly measured one.
        this.scrollingHeaderTable.remove()
        this.scrollingHeaderTable = this.createScrollingHeaderTable()
        this.scrollingHeaderTable.classList.add("flex-scrolling-header-table")
        this.scrollingHeaderTable.tHead.style.background = "white"
        this.verticalContainer.appendChild(this.scrollingHeaderTable)
        if (this.flexTableContext) this.flexTableContext.scrollingHeaderTable = this.scrollingHeaderTable

        // Re-affix with fresh geometry if we're still in the affix zone.
        if (this.shouldAffixHeader()) this.affixHeader()
        this.scrollingHeaderTable.scrollLeft = this.horizontalContainer.scrollLeft
    }

    shouldAffixHeader() {
        // Measure against the viewport so affixing works no matter which element is
        // scrolling — the window OR an inner overflow container (e.g. .apex-nav-body on
        // smaller/portrait layouts). The previous window.scrollY-based check never fired
        // when an ancestor scroller kept window.scrollY pinned at 0.
        // (For window scrolling this is mathematically identical to the old check.)
        const rect = this.verticalContainer.getBoundingClientRect()
        const headerTop = this.options.fixedHeaderTop || 0
        const minHeight = this.options.verticalScrollHeight || 0

        return rect.top < headerTop && rect.bottom > headerTop
            && this.table.scrollHeight > minHeight
    }

    affixHeader() {
        if (this.isHeaderAffixed) return
        this.scrollingHeaderTable.classList.add("affixed")
        this.scrollingHeaderTable.style.marginTop = this.options.fixedHeaderTop + "px"

        const marginLeft = offset(this.verticalContainer).left
        this.scrollingHeaderTable.style.marginLeft = marginLeft + this.horizontalContainer.offsetLeft + "px"

        this.scrollingHeaderTable.style.width = this.horizontalContainer.offsetWidth + "px"

        // The frozen header column cells aren't part of the fixed header, so position them manually.
        //TODO: This strategy doesn't work when there are multiple rows. Clone the frozen columns into
        // a separate table and implement that way for a more stable solution.
        this.table.querySelectorAll("th.freeze-column-cell")
            .forEach(cell => {
                cell.style.position = "fixed"
                cell.style.top = "0"
                cell.style.marginLeft = marginLeft + "px"
                // Ensure a white background.
                cell.style.backgroundColor = "#FFFFFF";

                cell.classList.add("fixed-cell")
            })

        this.isHeaderAffixed = true
    }

    detachHeader() {
        if (!this.isHeaderAffixed) return
        this.scrollingHeaderTable.classList.remove("affixed")

        this.table.querySelectorAll("th.freeze-column-cell")
            .forEach(cell => {
                cell.style.position = "absolute"
                cell.style.top = undefined
                cell.style.marginLeft = "0"

                cell.style.height = cell.parentElement.offsetHeight

                cell.classList.remove("fixed-cell")
            })

        this.isHeaderAffixed = false
    }

    /**
     * Create and return a cloned copy of the part of the table's head
     * that will scroll along with the content.
     * @returns {HTMLTableElement} The cloned copy of the table.
     */
    createScrollingHeaderTable() {
        // Clone the table
        const tableClone = this.table.cloneNode()
        const tHeadClone = this.cloneTheadContents();
        tableClone.appendChild(tHeadClone)

        // Apply compilers to the cloned header table.
        return this.compilers.reduce((table, compiler) => {
            return compiler(table)
        }, tableClone)
    }

    /**
     * Clone the thead contents of the table's tHead.
     */
    cloneTheadContents() {
        const maxWidth = computeMaxWidth(this.table.tHead)

        const start = Math.max(0, Number(this.options.freezeLeft) || 0)
        const end = Math.max(0, maxWidth - (Number(this.options.freezeRight) || 0) - 1)

        const tHeadClone = this.table.tHead.cloneNode();
        for (const tr of this.table.tHead.querySelectorAll("tr")) {
            const trClone = tr.cloneNode();
            // Only clone the cells that fall within the given range.
            const cells = Array.from(tr.querySelectorAll("th,td"))
                .filter((_, i) => i >= start && i <= end);
            for (let i = 0; i < cells.length; i++) {
                trClone.appendChild(cloneCell(cells[i]));
            }
            tHeadClone.appendChild(trClone);
        }
        return tHeadClone;
    }
}

//function containerScrollTop(el) {
//    return el.scrollTop === undefined ? el.scrollY : el.scrollTop
//}
